// src/main.jsx
import "./index.css";
import "./styles/subframe-integration.css";
import "./styles/mobile-optimized.css";
import "./styles/mobile-optimizations.css";
import "./styles/mobile-touch-targets.css";
// Phase 3: World-class polish
import "./styles/micro-animations.css";
import "./styles/typography-system.css";
import "./styles/grid-system.css";
import React from "react";
import ReactDOM from "react-dom/client";
// PostHog is lazy-loaded after initial render (see App component useEffect)

// Debug helpers
import { auth, db, COLLECTIONS } from './shared/utils/core/firebase-config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import logger from './shared/utils/core/logger';
import { trackSessionStarted } from './shared/utils/analytics/analytics';
import { trackException } from './shared/utils/analytics/posthog';
import { isNative } from './shared/utils/core/platform';
import { configureRevenueCat } from './features/subscription/services/subscriptionService';

// PWA Service Worker
import { register as registerSW, initNetworkMonitoring } from './shared/utils/pwa/serviceWorker';
import { installFetchTracker } from './shared/utils/core/fetchWrapper';
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Error Boundary
import ErrorBoundary, { withErrorBoundary, withAsyncErrorBoundary } from "./shared/components/ui/ErrorBoundary";

// Context Providers
import { AuthProvider, useAuth } from "./features/auth/context/AuthContext";
import { ProfileProvider } from "./features/profile/context/ProfileContext";
import { JournalProvider } from "./features/journal/context/JournalContext";
import { GoalsProvider } from "./features/goals/context/GoalsContext";
import { SubscriptionProvider } from "./features/subscription/context/SubscriptionContext";
import { OnboardingProvider } from "./features/onboarding/context/OnboardingContext";
import { IntentionsProvider } from "./features/intentions/context/IntentionsContext";
import { SubframeProvider } from "./theme/SubframeProvider";

// Layouts - Static imports (required for initial load)
import Layout from "./app/_layout";
import TabsLayout from "./app/(tabs)/_layout";
import NotFound from "./app/+not-found";

// Pages - Lazy loaded for bundle optimization (code-split)
const Dashboard = React.lazy(() => import("./app/(tabs)/index"));
const Insights = React.lazy(() => import("./features/insights/pages/InsightsPage"));
const Profile = React.lazy(() => import("./app/(tabs)/profile"));
const GoalsPage = React.lazy(() => import("./app/goals/index"));
const GoalDetailPage = React.lazy(() => import("./features/goals/pages/GoalDetailPage"));
const JournalEntry = React.lazy(() => import("./features/journal/pages/EntryDetailPage"));
const NewEntry = React.lazy(() => import("./features/journal/pages/NewEntryPage"));
const PostEntryReflection = React.lazy(() => import("./features/journal/pages/PostEntryReflectionPage"));
const LibraryPage = React.lazy(() => import("./features/library/pages/LibraryPage"));
const ReflectionDetailPage = React.lazy(() => import("./features/insights/pages/ReflectionDetailPage"));
const SettingsPage = React.lazy(() => import("./app/(tabs)/settings"));
const PrivacyPolicy = React.lazy(() => import("./app/legal/privacy"));
const TermsOfService = React.lazy(() => import("./app/legal/terms"));
const EmailActionHandler = React.lazy(() => import("./features/auth/pages/EmailActionHandler"));
const PaywallPage = React.lazy(() => import("./features/subscription/pages/PaywallPage"));
const SubscriptionManagePage = React.lazy(() => import("./features/subscription/pages/SubscriptionManagePage"));
const PlannerPage = React.lazy(() => import("./features/planner/pages/PlannerPage"));
const SharedEntryPage = React.lazy(() => import("./features/journal/pages/SharedEntryPage"));
const Login = React.lazy(() => import("./features/auth/components/login"));

// Wrap components with async error boundaries (includes Suspense + ErrorBoundary)
const SafeDashboard = withAsyncErrorBoundary(Dashboard, { 
  errorBoundaryProps: { userId: 'dashboard' },
  loadingMessage: "Loading dashboard..."
});
const SafeInsights = withAsyncErrorBoundary(Insights, { 
  errorBoundaryProps: { userId: 'insights' },
  loadingMessage: "Loading insights..."
});
const SafeProfile = withAsyncErrorBoundary(Profile, { 
  errorBoundaryProps: { userId: 'profile' },
  loadingMessage: "Loading profile..."
});
const SafeGoals = withAsyncErrorBoundary(GoalsPage, {
  errorBoundaryProps: { userId: 'goals' },
  loadingMessage: "Loading goals..."
});
const SafeGoalDetail = withAsyncErrorBoundary(GoalDetailPage, {
  errorBoundaryProps: { userId: 'goal-detail' },
  loadingMessage: "Loading goal details..."
});
const SafeJournalEntry = withAsyncErrorBoundary(JournalEntry, { 
  errorBoundaryProps: { userId: 'journal-entry' },
  loadingMessage: "Loading journal entry..."
});
const SafeNewEntry = withAsyncErrorBoundary(NewEntry, {
  errorBoundaryProps: { userId: 'new-entry' },
  loadingMessage: "Preparing new entry..."
});
const SafePostEntryReflection = withAsyncErrorBoundary(PostEntryReflection, {
  errorBoundaryProps: { userId: 'post-entry-reflection' },
  loadingMessage: "Loading reflection..."
});
// OCR/Handwritten functionality integrated into New Entry via Media button
const SafeLibrary = withAsyncErrorBoundary(LibraryPage, {
  errorBoundaryProps: { userId: 'library' },
  loadingMessage: "Loading library..."
});
const SafeReflectionDetail = withAsyncErrorBoundary(ReflectionDetailPage, {
  errorBoundaryProps: { userId: 'reflection-detail' },
  loadingMessage: "Loading reflection..."
});
const SafeSettings = withAsyncErrorBoundary(SettingsPage, {
  errorBoundaryProps: { userId: 'settings' },
  loadingMessage: "Loading settings..."
});
const SafePrivacy = withAsyncErrorBoundary(PrivacyPolicy, {
  errorBoundaryProps: { userId: 'privacy' },
  loadingMessage: "Loading privacy policy..."
});
const SafeTerms = withAsyncErrorBoundary(TermsOfService, { 
  errorBoundaryProps: { userId: 'terms' },
  loadingMessage: "Loading terms of service..."
});
const SafeLogin = withAsyncErrorBoundary(Login, {
  errorBoundaryProps: { userId: 'login' },
  loadingMessage: "Loading login..."
});
const SafeEmailActionHandler = withAsyncErrorBoundary(EmailActionHandler, {
  errorBoundaryProps: { userId: 'email-action' },
  loadingMessage: "Processing..."
});
const SafePaywall = withAsyncErrorBoundary(PaywallPage, {
  errorBoundaryProps: { userId: 'paywall' },
  loadingMessage: "Loading..."
});
const SafeSubscriptionManage = withAsyncErrorBoundary(SubscriptionManagePage, {
  errorBoundaryProps: { userId: 'subscription-manage' },
  loadingMessage: "Loading..."
});
const SafePlannerPage = withAsyncErrorBoundary(PlannerPage, {
  errorBoundaryProps: { userId: 'planner' },
  loadingMessage: "Loading planner..."
});
const SafeSharedEntry = withAsyncErrorBoundary(SharedEntryPage, {
  errorBoundaryProps: { userId: 'shared-entry' },
  loadingMessage: "Loading entry..."
});

// Also wrap Layout components with async error boundaries
const SafeLayout = withAsyncErrorBoundary(Layout, {
  errorBoundaryProps: { userId: 'layout' },
  loadingMessage: "Loading application..."
});
const SafeTabsLayout = withAsyncErrorBoundary(TabsLayout, {
  errorBoundaryProps: { userId: 'tabs-layout' },
  loadingMessage: "Loading navigation..."
});

// Wrapper to provide user to OnboardingProvider
function OnboardingWrapper({ children }) {
  const { user } = useAuth();
  return (
    <OnboardingProvider user={user}>
      {children}
    </OnboardingProvider>
  );
}

// Combined App Provider for cleaner nesting
function AppProviders({ children }) {
  return (
    <ErrorBoundary>
      <SubframeProvider>
        <AuthProvider>
          <OnboardingWrapper>
            <ProfileProvider>
              <SubscriptionProvider>
                <JournalProvider>
                    <GoalsProvider>
                      <IntentionsProvider>
                        {children}
                      </IntentionsProvider>
                    </GoalsProvider>
                </JournalProvider>
              </SubscriptionProvider>
            </ProfileProvider>
          </OnboardingWrapper>
        </AuthProvider>
      </SubframeProvider>
    </ErrorBoundary>
  );
}

// App Router Component
function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<SafeLayout />}>
          {/* Tabs: Journal, Insights, Profile */}
          <Route element={<SafeTabsLayout />}>
            <Route index element={<SafeDashboard />} />
            <Route path="insights" element={<SafeInsights />} />
            <Route path="profile" element={<SafeProfile />} />
          </Route>
          
          {/* Goals */}
          <Route path="goals" element={<SafeGoals />} />
          <Route path="goals/:id" element={<SafeGoalDetail />} />

          {/* Library */}
          <Route path="library" element={<SafeLibrary />} />

          {/* Settings */}
          <Route path="settings" element={<SafeSettings />} />
          
          {/* Journal */}
          <Route path="journal">
            <Route path="entry/:id" element={<SafeJournalEntry />} />
            <Route path="new" element={<SafeNewEntry />} />
            <Route path="post-entry" element={<SafePostEntryReflection />} />
          </Route>

          {/* Paywall */}
          <Route path="paywall" element={<SafePaywall />} />

          {/* Subscription management */}
          <Route path="subscription/manage" element={<SafeSubscriptionManage />} />

          {/* Planner */}
          <Route path="planner" element={<SafePlannerPage />} />

          {/* Reflections */}
          <Route path="reflection/:id" element={<SafeReflectionDetail />} />

          {/* Shared entries (public, no auth required) */}
          <Route path="shared/:shareToken" element={<SafeSharedEntry />} />

          {/* Legal */}
          <Route path="legal">
            <Route path="privacy" element={<SafePrivacy />} />
            <Route path="terms" element={<SafeTerms />} />
          </Route>
          
          {/* Auth */}
          <Route path="login" element={<SafeLogin />} />
          <Route path="auth/action" element={<SafeEmailActionHandler />} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

// Initialize app with error tracking and PWA features
function initializeApp() {
  // Note: PostHog is initialized via PostHogProvider in the render tree

  // Track all Cloud Function API calls (success, failure, duration)
  installFetchTracker();

  // Set up global error handlers
  window.addEventListener('unhandledrejection', (event) => {
    if (import.meta.env.DEV) {
      logger.error('Unhandled promise rejection:', event.reason);
    }
    // Send to PostHog for error tracking
    if (event.reason instanceof Error) {
      trackException(event.reason, { type: 'unhandled_rejection' });
    }
  });

  window.addEventListener('error', (event) => {
    if (import.meta.env.DEV) {
      logger.error('Global error:', event.error);
    }
    // Send to PostHog for error tracking
    if (event.error instanceof Error) {
      trackException(event.error, { type: 'global_error' });
    }
  });

  // Initialize PWA features (install prompt handled by PWAInstallBanner React component)
  initNetworkMonitoring();

  // Register service worker (only in production, not in native apps)
  if (import.meta.env.PROD && 'serviceWorker' in navigator && !isNative()) {
    registerSW({
      onSuccess: (registration) => {
        logger.debug('PWA: App is ready for offline use');

        // Show success notification
        const event = new CustomEvent('pwa-ready', {
          detail: { message: 'App is ready for offline use!' }
        });
        window.dispatchEvent(event);
      },
      onUpdate: (registration) => {
        logger.debug('PWA: New content available, please refresh');

        // Show update notification
        const event = new CustomEvent('pwa-update', {
          detail: {
            message: 'New version available! Please refresh to update.',
            registration: registration
          }
        });
        window.dispatchEvent(event);
      }
    });
  }

  // App initialized with PWA features
}

// Expose cleanup utility for debugging
import { cleanupIndexedDBNow } from './shared/utils/cleanup/cleanupIndexedDB';
if (import.meta.env.DEV) {
  window.cleanupIndexedDB = cleanupIndexedDBNow;
}

// App component with initialization
function App() {
  React.useEffect(() => {
    initializeApp();

    // Track session start
    trackSessionStarted();

    // Lazy-load PostHog after initial render to keep it out of the critical bundle
    const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
    if (posthogKey) {
      import('posthog-js').then(({ default: posthog }) => {
        if (!posthog.__loaded) {
          posthog.init(posthogKey, {
            api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
            defaults: '2025-11-30',
            autocapture: true,
            capture_pageview: true,
            capture_pageleave: true,
            session_recording: {
              maskAllInputs: true,
              maskTextSelector: '[data-mask]',
            },
          });
        }
      });
    }

    // Configure RevenueCat SDK early (no user yet — SubscriptionContext will logIn after auth)
    if (isNative()) configureRevenueCat(null);
  }, []);

  return (
    <AppProviders>
      <AppRouter />
    </AppProviders>
  );
}

// Render app (PostHog is lazy-loaded inside App useEffect)
const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);


// ─────────────────────────────────────────────────────────────
// Environment validation (for development)
if (import.meta.env.DEV) {
  const requiredEnvVars = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_STORAGE_BUCKET',
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    'VITE_FIREBASE_APP_ID'
  ];

  const missingVars = requiredEnvVars.filter(varName => !import.meta.env[varName]);
  
  if (missingVars.length > 0) {
    // Missing required environment variables
  }

  // Optional environment variables
  const optionalVars = ['VITE_AI_PROXY_URL', 'VITE_OCR_PROXY_URL'];
  const missingOptional = optionalVars.filter(varName => !import.meta.env[varName]);
  
  if (missingOptional.length > 0) {
    // Optional environment variables not set - using default endpoints
  }

  // Expose debug helpers to console
  window.debugClarity = {
    auth,
    db,
    doc,
    getDoc,
    setDoc,
    checkAuth: () => {
      const user = auth.currentUser;
      logger.debug('🔐 Auth Status:', user ? {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified
      } : '❌ NOT LOGGED IN');
      return user;
    },
    testFirestoreRead: async () => {
      try {
        const snap = await getDoc(doc(db, COLLECTIONS.HEXAGRAMS, '1'));
        logger.debug('✅ Firestore READ works:', snap.exists() ? 'Found hexagram 1' : 'Not found');
        return true;
      } catch (e) {
        logger.debug('❌ Firestore READ failed:', e.code, e.message);
        return false;
      }
    },
    testFirestoreWrite: async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          logger.debug('❌ Cannot test write: Not logged in');
          return false;
        }
        await setDoc(doc(db, COLLECTIONS.USER_PROFILES, user.uid), {
          test: 'debug test',
          timestamp: new Date()
        }, { merge: true });
        logger.debug('✅ Firestore WRITE works!');
        return true;
      } catch (e) {
        logger.debug('❌ Firestore WRITE failed:', e.code, e.message);
        return false;
      }
    }
  };

  logger.debug('🐛 Debug helpers available! Try:');
  logger.debug('  debugClarity.checkAuth()');
  logger.debug('  debugClarity.testFirestoreRead()');
  logger.debug('  debugClarity.testFirestoreWrite()');
}